<template lang="pug">
    include ../../icon/arrow

    ._ovfl
        ._ovfs
            transition(name="_ovfto", :duration="500")
                ib-button.link.mirror-rtl.text-start(
                    v-if="arrows",
                    v-show="moreBack",
                    :class="$rtl ? '_ovfbr' : '_ovfbl'",
                    aria-hidden="true",
                    @click="back"
                )
                    +icon-arrow-chevron_round_left
        ._ovfm(
            ref="main",
            :class="mainClass",
            v-size.width="refresh",
            @scroll="refresh"
        )
            slot
        ._ovfe
            transition(name="_ovfto", :duration="500")
                ib-button.link.mirror-rtl.text-end(
                    v-if="arrows",
                    v-show="moreNext",
                    :class="$rtl ? '_ovfbl' : '_ovfbr'",
                    aria-hidden="true",
                    @click="next"
                )
                    +icon-arrow-chevron_round_right
</template>

<script>
    import $device from '../../global/device';
    import { minScrollbarHeight } from '../../init';

    const syncThrottle = 72;

    /**
     * This is a container that displays arrow buttons when content horizontally overflows and the primary pointing
     * device is a mouse. On touch devices, overflow will be handled with an invisible scrollbar, allowing touch drag.
     * This container is used internally to enclose [ib-tab-bar](#/components/layout/ib-tab-bar) tabs, for example.
     * 
     * This component may be used with [.ib-col](#/components/layout/ib-col) or [.ib-row](#/components/layout/ib-row)
     * classes.
     */
    export default {
        name: 'ib-overflow',

        data() {
            return {
                moreBack: false,
                moreNext: false
            };
        },

        computed: {
            arrows() {
                return $device.mouse;
            },

            sp() {
                return $util.scrollProps(this.$rtl);
            },

            mainClass() {
                const rtl = this.$rtl;
                const arrows = this.arrows;
                const moreBack = this.moreBack;
                const moreNext = this.moreNext;
                return {
                    _ovfml: arrows && (rtl ? moreNext : moreBack),
                    _ovfmr: arrows && (rtl ? moreBack : moreNext),
                    _ovfch: minScrollbarHeight > 0
                };
            }
        },

        mounted() {
            (this.mo = new MutationObserver(this.refresh))
                .observe(this.$refs.main, {
                    characterData: true,
                    childList: true,
                    subtree: true
                });
        },

        beforeDestroy() {
            this.mo?.disconnect();
            this.syncto && clearTimeout(this.syncto);
        },

        methods: {
            refresh() {
                if (!this.syncto) {
                    this.syncto = setTimeout(() => {
                        this.syncto = 0;
                        const main = this.$refs.main;
                        if (main) {
                            const overflow = main.scrollWidth - main.clientWidth;
                            const left = main.scrollLeft;
                            const base = this.sp.base;
                            const step = this.sp.step;
                            this.moreBack = overflow > 0 && step * left > step * overflow * base + 1;
                            this.moreNext = overflow > 0 && step * left < step * overflow * (base + step) - 1;
                        }
                    }, syncThrottle);
                }
            },

            back() {
                const main = this.$refs.main;
                main.scrollBy({ left: -this.sp.step * main.clientWidth, behavior: 'smooth' });
            },

            next() {
                const main = this.$refs.main;
                main.scrollBy({ left: this.sp.step * main.clientWidth, behavior: 'smooth' });
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._ovfl {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        align-items: stretch;
    }
    ._ovfs,
    ._ovfe {
        position: relative;
        display: flex;
        align-items: stretch;
        width: 0;
        z-index: 1;
        opacity: 1;
    }
    ._ovfs {
        justify-content: flex-start;
    }
    ._ovfe {
        justify-content: flex-end;
    }
    ._ovfbl,
    ._ovfbr {
        flex: 0 0 40px;
        padding: 0 4px !important;
    }
    ._ovfbl {
        background-image: linear-gradient(90deg, @bg 0, fade(@bg, 0));
    }
    ._ovfbr {
        background-image: linear-gradient(270deg, @bg 0, fade(@bg, 0));
    }
    ._ovfm {
        position: relative;
        overflow-x: scroll;
        overflow-y: hidden;
        flex: 0 0 100%;
        align-self: baseline;
        height: 100%;
        z-index: 0;
        .hide-scrollbar();
    }
    ._ovfch {
        overflow-x: hidden;
    }
    .ib-row > ._ovfm {
        display: flex;
        flex-direction: row;
    }
    ._ovfto {
        &-enter,
        &-leave-to {
            opacity: 0;
        }
        &-enter-active,
        &-leave-active {
            transition: opacity .5s;
        }
    }
</style>

<ib-example id="basic" name="Basic">
    <template lang="pug">
        ib-overflow.nowrap
            ib-button(v-for="i in 32", :key="i", width="64px") {{i}}
    </template>
</ib-example>
